<template>
  <AdminLayout page-title="Pricing Plans" page-subtitle="Manage subscription plans displayed on the landing page">
    <div class="space-y-8">
      <!-- Header Actions -->
      <div class="flex justify-end">
        <button @click="showPlanModal = true" class="btn-hero-primary">
          <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
          </svg>
          Add New Plan
        </button>
      </div>

      <!-- Plans Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div v-for="plan in plans" :key="plan.id" class="plan-card group">
          <div class="plan-header">
            <div class="flex justify-between items-start mb-4">
              <div class="flex-1">
                <h3 class="plan-title">{{ plan.name }}</h3>
                <p class="plan-description">{{ plan.description }}</p>
              </div>
              <div class="flex space-x-2">
                <button @click="editPlan(plan)" class="btn-icon">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                  </svg>
                </button>
                <button @click="deletePlan(plan.id)" class="btn-icon btn-icon-danger">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                  </svg>
                </button>
              </div>
            </div>
            
            <div class="plan-price mb-4">
              <span class="price-amount">${{ plan.price }}</span>
              <span class="price-period">/{{ plan.billingPeriod }}</span>
            </div>
            
            <div class="plan-badge" :class="plan.popular ? 'badge-popular' : 'badge-default'">
              {{ plan.popular ? 'Most Popular' : 'Standard' }}
            </div>
          </div>

          <div class="plan-body">
            <div class="plan-features mb-6">
              <h4 class="features-title">Features:</h4>
              <ul class="features-list">
                <li v-for="feature in plan.features" :key="feature" class="feature-item">
                  <svg class="w-4 h-4 text-emerald-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                  </svg>
                  {{ feature }}
                </li>
              </ul>
            </div>

            <div class="plan-limits mb-6" v-if="plan.limits">
              <h4 class="limits-title">Limits:</h4>
              <div class="limits-grid">
                <div v-if="plan.limits.jobs" class="limit-item">
                  <span class="limit-label">Jobs per month:</span>
                  <span class="limit-value">{{ plan.limits.jobs === -1 ? 'Unlimited' : plan.limits.jobs }}</span>
                </div>
                <div v-if="plan.limits.users" class="limit-item">
                  <span class="limit-label">Team members:</span>
                  <span class="limit-value">{{ plan.limits.users === -1 ? 'Unlimited' : plan.limits.users }}</span>
                </div>
                <div v-if="plan.limits.storage" class="limit-item">
                  <span class="limit-label">Storage:</span>
                  <span class="limit-value">{{ plan.limits.storage === -1 ? 'Unlimited' : plan.limits.storage + 'GB' }}</span>
                </div>
              </div>
            </div>

            <div class="plan-status">
              <label class="flex items-center">
                <input 
                  type="checkbox" 
                  v-model="plan.active" 
                  @change="updatePlanStatus(plan.id, plan.active)"
                  class="form-checkbox"
                >
                <span class="ml-3 text-sm text-slate-300">Active on landing page</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <!-- Plan Modal -->
      <div v-if="showPlanModal" class="modal-overlay">
        <div class="modal-content">
          <div class="modal-header">
            <h3 class="modal-title">{{ editingPlan ? 'Edit Plan' : 'Add New Plan' }}</h3>
            <button @click="closePlanModal" class="modal-close">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div class="modal-body">
            <form @submit.prevent="savePlan">
              <div class="form-grid">
                <div class="form-group">
                  <label class="form-label">Plan Name</label>
                  <input v-model="planForm.name" type="text" class="form-input" required>
                </div>

                <div class="form-group">
                  <label class="form-label">Description</label>
                  <input v-model="planForm.description" type="text" class="form-input" required>
                </div>

                <div class="form-group">
                  <label class="form-label">Price ($)</label>
                  <input v-model.number="planForm.price" type="number" step="0.01" class="form-input" required>
                </div>

                <div class="form-group">
                  <label class="form-label">Billing Period</label>
                  <select v-model="planForm.billingPeriod" class="form-input" required>
                    <option value="month">Monthly</option>
                    <option value="year">Yearly</option>
                    <option value="week">Weekly</option>
                  </select>
                </div>

                <div class="form-group col-span-2">
                  <label class="form-label">Features (one per line)</label>
                  <textarea 
                    v-model="planForm.featuresText" 
                    class="form-textarea" 
                    rows="5"
                    placeholder="Enter features, one per line"
                  ></textarea>
                </div>

                <div class="form-group">
                  <label class="form-label">Jobs per month (-1 for unlimited)</label>
                  <input v-model.number="planForm.limits.jobs" type="number" class="form-input">
                </div>

                <div class="form-group">
                  <label class="form-label">Team members (-1 for unlimited)</label>
                  <input v-model.number="planForm.limits.users" type="number" class="form-input">
                </div>

                <div class="form-group">
                  <label class="form-label">Storage GB (-1 for unlimited)</label>
                  <input v-model.number="planForm.limits.storage" type="number" class="form-input">
                </div>

                <div class="form-group">
                  <label class="form-label">Display Order</label>
                  <input v-model.number="planForm.order" type="number" class="form-input">
                </div>

                <div class="form-group col-span-2">
                  <label class="flex items-center">
                    <input type="checkbox" v-model="planForm.popular" class="form-checkbox">
                    <span class="ml-3 text-slate-300">Mark as "Most Popular"</span>
                  </label>
                </div>

                <div class="form-group col-span-2">
                  <label class="flex items-center">
                    <input type="checkbox" v-model="planForm.active" class="form-checkbox">
                    <span class="ml-3 text-slate-300">Active on landing page</span>
                  </label>
                </div>
              </div>

              <div class="modal-actions">
                <button type="button" @click="closePlanModal" class="btn-outline">Cancel</button>
                <button type="submit" class="btn-hero-primary">{{ editingPlan ? 'Update Plan' : 'Create Plan' }}</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </AdminLayout>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import AdminLayout from './AdminLayout.vue'

interface PlanLimits {
  jobs?: number
  users?: number
  storage?: number
}

interface Plan {
  id: string
  name: string
  description: string
  price: number
  billingPeriod: 'month' | 'year' | 'week'
  features: string[]
  limits?: PlanLimits
  popular: boolean
  active: boolean
  order: number
}

// State
const showPlanModal = ref(false)
const editingPlan = ref<Plan | null>(null)
const plans = ref<Plan[]>([])

// Form
const planForm = reactive({
  name: '',
  description: '',
  price: 0,
  billingPeriod: 'month' as 'month' | 'year' | 'week',
  featuresText: '',
  limits: {
    jobs: -1,
    users: -1,
    storage: -1
  },
  popular: false,
  active: true,
  order: 0
})

// Methods
const loadPlans = () => {
  // TODO: Replace with actual API call
  plans.value = [
    {
      id: '1',
      name: 'Starter',
      description: 'Perfect for individual contractors',
      price: 29,
      billingPeriod: 'month',
      features: [
        'Job marketplace access',
        'Basic scheduling',
        'Digital timesheets',
        'Expense tracking',
        'Mobile app access'
      ],
      limits: {
        jobs: 10,
        users: 1,
        storage: 5
      },
      popular: false,
      active: true,
      order: 1
    },
    {
      id: '2', 
      name: 'Professional',
      description: 'Ideal for growing contractors',
      price: 79,
      billingPeriod: 'month',
      features: [
        'Everything in Starter',
        'Advanced scheduling',
        'Team management',
        'Compliance tracking',
        'Priority support',
        'Custom reporting'
      ],
      limits: {
        jobs: 50,
        users: 5,
        storage: 25
      },
      popular: true,
      active: true,
      order: 2
    },
    {
      id: '3',
      name: 'Enterprise',
      description: 'For large operations and companies',
      price: 199,
      billingPeriod: 'month',
      features: [
        'Everything in Professional',
        'Unlimited jobs',
        'Unlimited team members',
        'Advanced analytics',
        'API access',
        'Dedicated support',
        'Custom integrations'
      ],
      limits: {
        jobs: -1,
        users: -1,
        storage: -1
      },
      popular: false,
      active: true,
      order: 3
    }
  ]
}

const editPlan = (plan: Plan) => {
  editingPlan.value = plan
  planForm.name = plan.name
  planForm.description = plan.description
  planForm.price = plan.price
  planForm.billingPeriod = plan.billingPeriod
  planForm.featuresText = plan.features.join('\n')
  planForm.limits = { ...plan.limits } || { jobs: -1, users: -1, storage: -1 }
  planForm.popular = plan.popular
  planForm.active = plan.active
  planForm.order = plan.order
  showPlanModal.value = true
}

const savePlan = () => {
  const features = planForm.featuresText.split('\n').filter(f => f.trim())
  
  const planData: Omit<Plan, 'id'> = {
    name: planForm.name,
    description: planForm.description,
    price: planForm.price,
    billingPeriod: planForm.billingPeriod,
    features,
    limits: planForm.limits,
    popular: planForm.popular,
    active: planForm.active,
    order: planForm.order
  }

  if (editingPlan.value) {
    // Update existing plan
    const index = plans.value.findIndex(p => p.id === editingPlan.value!.id)
    if (index !== -1) {
      plans.value[index] = { ...planData, id: editingPlan.value.id }
    }
  } else {
    // Create new plan
    const newPlan: Plan = {
      ...planData,
      id: Date.now().toString()
    }
    plans.value.push(newPlan)
  }

  // TODO: Save to API
  console.log('Saving plan:', planData)
  
  closePlanModal()
}

const deletePlan = (planId: string) => {
  if (confirm('Are you sure you want to delete this plan?')) {
    plans.value = plans.value.filter(p => p.id !== planId)
    // TODO: Delete from API
    console.log('Deleting plan:', planId)
  }
}

const updatePlanStatus = (planId: string, active: boolean) => {
  // TODO: Update API
  console.log('Updating plan status:', planId, active)
}

const closePlanModal = () => {
  showPlanModal.value = false
  editingPlan.value = null
  // Reset form
  Object.assign(planForm, {
    name: '',
    description: '',
    price: 0,
    billingPeriod: 'month',
    featuresText: '',
    limits: { jobs: -1, users: -1, storage: -1 },
    popular: false,
    active: true,
    order: 0
  })
}

// Lifecycle
onMounted(() => {
  loadPlans()
})
</script>

<style scoped>
/* Button Styles */
.btn-hero-primary {
  @apply px-6 py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-xl hover:from-emerald-600 hover:to-cyan-600 transition-all duration-300 shadow-lg shadow-emerald-500/25 inline-flex items-center;
}

.btn-outline {
  @apply px-4 py-2 border border-slate-600 text-slate-300 rounded-xl hover:border-emerald-500 hover:text-emerald-400 transition-all duration-300;
}

.btn-icon {
  @apply p-2 text-slate-400 hover:text-slate-200 rounded-lg hover:bg-slate-700/50 transition-colors;
}

.btn-icon-danger {
  @apply hover:text-red-400 hover:bg-red-500/10;
}

/* Plan Cards */
.plan-card {
  @apply bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-3xl hover:border-emerald-500/30 transition-all duration-500 hover:transform hover:scale-105 hover:shadow-2xl hover:shadow-emerald-500/10 relative mt-4;
}

.plan-header {
  @apply p-6 pt-8 border-b border-slate-700/50 bg-slate-800/20 relative;
}

.plan-title {
  @apply text-xl font-bold text-white mb-2;
}

.plan-description {
  @apply text-sm text-slate-400;
}

.plan-price {
  @apply flex items-baseline;
}

.price-amount {
  @apply text-3xl font-bold text-white;
}

.price-period {
  @apply text-slate-400 ml-2;
}

.plan-badge {
  @apply absolute -top-3 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-full text-xs font-medium shadow-lg z-10;
}

.badge-popular {
  @apply bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 text-emerald-400 border border-emerald-500/30;
}

.badge-default {
  @apply bg-slate-700/50 text-slate-300 border border-slate-600/50;
}

.plan-body {
  @apply p-6;
}

.features-title {
  @apply font-semibold text-white mb-4;
}

.features-list {
  @apply space-y-3;
}

.feature-item {
  @apply flex items-start text-sm text-slate-300;
}

.limits-title {
  @apply font-semibold text-white mb-4;
}

.limits-grid {
  @apply space-y-3;
}

.limit-item {
  @apply flex justify-between text-sm;
}

.limit-label {
  @apply text-slate-400;
}

.limit-value {
  @apply font-medium text-white;
}

/* Modal Styles */
.modal-overlay {
  @apply fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50;
}

.modal-content {
  @apply bg-slate-800/90 backdrop-blur-sm border border-slate-700/50 rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto;
}

.modal-header {
  @apply flex justify-between items-center p-6 border-b border-slate-700/50;
}

.modal-title {
  @apply text-xl font-bold text-white;
}

.modal-close {
  @apply text-slate-400 hover:text-white transition-colors p-2 hover:bg-slate-700/50 rounded-lg;
}

.modal-body {
  @apply p-6;
}

/* Form Styles */
.form-grid {
  @apply grid grid-cols-1 md:grid-cols-2 gap-6 mb-8;
}

.form-group {
  @apply space-y-2;
}

.form-label {
  @apply block text-sm font-medium text-white;
}

.form-input {
  @apply w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300;
}

.form-textarea {
  @apply w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300 resize-y;
}

.form-checkbox {
  @apply rounded text-emerald-500 bg-slate-700 border-slate-600 focus:ring-emerald-500 focus:ring-offset-0;
}

.modal-actions {
  @apply flex justify-end space-x-4 pt-6 border-t border-slate-700/50;
}

.col-span-2 {
  grid-column: span 2;
}

@media (max-width: 768px) {
  .col-span-2 {
    grid-column: span 1;
  }
}
</style>
